import { type ReactNode } from 'react'
import { Building2, ExternalLink } from 'lucide-react'
import { ListCard } from '@/components/list-card'
import { ListCardHeader, type ListCardHeaderBadge } from '@/components/list-card-header'
import { EntityLogo } from '@/components/ui/entity-logo'
import { markdownPlainTextPreview } from '@/lib/markdown-display'

export type OrganisationCardProps = {
  name: string
  slug: string
  description?: string | null
  website?: string | null
  industry?: string | null
  logoUrl?: string | null
  onOpen?: () => void
  openAriaLabel?: string
  footerNote?: string | null
  className?: string
  trailing?: ReactNode
}

/**
 * Employer organisation row card; composes {@link ListCard} for the managed org list
 * and other surfaces that need a consistent logo + metadata layout.
 */
export function OrganisationCard({
  name,
  slug,
  description,
  website,
  industry,
  logoUrl,
  onOpen,
  openAriaLabel,
  footerNote,
  className,
  trailing,
}: OrganisationCardProps) {
  const trimmedDescription = markdownPlainTextPreview(description, [name])
  const trimmedWebsite = website?.trim() ?? ''
  const trimmedIndustry = industry?.trim() ?? ''
  const logoSrc = logoUrl?.trim() ?? ''

  const industryBadge: ListCardHeaderBadge | null = trimmedIndustry
    ? { label: trimmedIndustry, variant: 'secondary' }
    : null

  const header = (
    <div className="flex min-w-0 items-start justify-between gap-4">
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <EntityLogo src={logoSrc || null} alt="" size="thumb" />
        <ListCardHeader title={name} badge={industryBadge} className="min-w-0 flex-1" />
      </div>
      {trailing ? <div className="pointer-events-auto shrink-0">{trailing}</div> : null}
    </div>
  )

  const primaryLine = trimmedDescription || `/${slug}`

  const body = (
    <div className="space-y-3">
      <p className="pointer-events-none line-clamp-2 text-sm text-muted-foreground">{primaryLine}</p>
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        {trimmedDescription ? (
          <div className="flex items-center gap-1">
            <Building2 className="h-4 w-4 shrink-0" aria-hidden />
            <span>/{slug}</span>
          </div>
        ) : null}
        {trimmedWebsite ? (
          <div className="pointer-events-auto flex min-w-0 items-center gap-1">
            <ExternalLink className="pointer-events-none h-4 w-4 shrink-0" aria-hidden />
            <a
              href={trimmedWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {trimmedWebsite}
            </a>
          </div>
        ) : null}
        {!trimmedDescription && !trimmedWebsite ? (
          <div className="flex items-center gap-1">
            <Building2 className="h-4 w-4 shrink-0" aria-hidden />
            <span>Employer organisation</span>
          </div>
        ) : null}
      </div>
    </div>
  )

  const footer = footerNote?.trim() ? (
    <p className="text-xs text-muted-foreground">{footerNote.trim()}</p>
  ) : null

  return (
    <ListCard
      header={header}
      body={body}
      footer={footer}
      onOpen={onOpen}
      openAriaLabel={openAriaLabel ?? `Open organisation ${name}`}
      className={className}
    />
  )
}
